import type { BaseObject } from './types.js'

/**
 * One-on-One Coach (Aria) shared contracts. Distinct from the post-game debrief coach
 * (`ai_coach_sessions`). All FE + BE code imports these; never redefine locally.
 */

/** Which role a coach persona/prompt targets. GAMER and LEADER ship; the others are forward-compat. */
export enum CoachRole {
  GAMER = 'gamer',
  FACILITATOR = 'facilitator',
  LEADER = 'leader',
  ADMIN = 'admin'
}

/** Where a goal came from. Extendable so goals can later be added by non-coach systems. */
export enum GoalSource {
  ONBOARDING = 'onboarding',
  COACH_FLOW = 'coach_flow',
  FACILITATOR = 'facilitator',
  SYSTEM = 'system'
}

/** Which of the person's two goal lists a goal belongs to. Each list has its own cap. */
export enum GoalScope {
  INDIVIDUAL = 'individual',
  LEADERSHIP = 'leadership'
}

export enum GoalStatus {
  ACTIVE = 'active',
  ACHIEVED = 'achieved',
  ARCHIVED = 'archived'
}

/** A single development goal linked to a user. Stored in `user_goals`. */
export type UserGoal = BaseObject & {
  userId: string
  /** Human-readable goal, e.g. "Get better at giving direct feedback". */
  goal: string
  priority?: number
  status: GoalStatus
  source: GoalSource
  /** Absent means INDIVIDUAL, so goals stored before scopes existed keep their meaning. */
  scope?: GoalScope
  /** Coach conversation that produced it, when source is coach-driven. */
  conversationId?: string
  /** When the goal was marked achieved. Recorded now so progress can be shown later. */
  achievedAt?: string
  updatedAt?: string
}

/** Conversation flow so the coach knows the context of a talk. Stored on the conversation. */
export enum CoachConversationFlow {
  ONBOARDING = 'onboarding',
  /** Goal-setting for someone who has already played: the session is the material to set goals from. */
  ONBOARDING_AFTER_GAME = 'onboarding_after_game',
  POST_GAME = 'post_game',
  FREE = 'free',
  /** Leadership coaching: how the person leads, rather than their own game. */
  LEADERSHIP = 'leadership',
  /** A talk with Aria in the floating assistant, on any page. Kept so she remembers it; not listed on the coach page. */
  ASSISTANT = 'assistant'
}

export enum CoachConversationMode {
  TEXT = 'text',
  VOICE = 'voice'
}

export enum CoachMessageRole {
  USER = 'user',
  ASSISTANT = 'assistant'
}

/**
 * The complete set of tools a spoken coach session may call — the whitelist, shared by the backend
 * declarations and the frontend dispatcher so the two cannot drift.
 *
 * There is deliberately no navigation: leaving the coach page ends the conversation,
 * and on the next visit she reads that she was taking them somewhere and does it again. She names where
 * to click instead. Saving goals is a tool because a spoken session returns audio rather than JSON, and
 * a participant who simply leaves never ends a conversation, so agreed goals were otherwise lost.
 */
export enum CoachToolName {
  SAVE_DEVELOPMENT_GOALS = 'save_development_goals',
  /** Retire a goal the person says they are done with, to make room for a new one. Never without their word. */
  COMPLETE_DEVELOPMENT_GOAL = 'complete_development_goal',
  /** Hang up once the talk is genuinely finished, rather than leaving the microphone open on silence. */
  END_CONVERSATION = 'end_conversation',
  /** Keep what they asked her to remember, or how to coach them, the moment they say it; named as the assistant's. */
  REMEMBER_FOR_LATER = 'remember_for_later'
}

export const COACH_TOOL_NAME_LIST: CoachToolName[] = Object.values(CoachToolName)

/**
 * A single message in a conversation. `content` is stored ENCRYPTED at rest; the plaintext
 * shape below is what callables return to the owning user after server-side decryption.
 */
export type CoachMessage = BaseObject & {
  conversationId: string
  role: CoachMessageRole
  content: string
  /** True when the content was produced from a voice turn's transcription. */
  fromVoice?: boolean
  /** On a typed message: the id the page gave it when sending. */
  clientMessageId?: string
  /** On Aria's typed reply: the id of the message she answered. */
  replyToMessageId?: string
}

/** Encrypted-at-rest persisted form of a message (what actually lives in Firestore). */
export type StoredCoachMessage = Omit<CoachMessage, 'content'> & {
  /** Ciphertext + params produced by the backend message crypto helper. */
  encryptedContent: EncryptedPayload
}

export type EncryptedPayload = {
  /** Cipher scheme id, e.g. "aes-256-gcm:v1". */
  scheme: string
  iv: string
  authTag: string
  ciphertext: string
}

/** A coach conversation. Metadata is readable; transcript lives in the `messages` subcollection. */
export type CoachConversation = BaseObject & {
  userId: string
  organizationId?: string
  role: CoachRole
  flow: CoachConversationFlow
  /** The goal list this talk works on. Absent means the one its flow implies, so stored talks keep their meaning. */
  goalScope?: GoalScope
  mode: CoachConversationMode
  /** Session this talk is about (post-game flow), when applicable. */
  relatedSessionId?: string
  startedAt: string
  /** Set when the conversation is closed/resumed-past-grace. */
  endedAt?: string
  /** Last activity; drives the resume grace window. */
  lastMessageAt?: string
  /** Start of the current sitting; reset when the thread is picked up after a long idle gap. */
  sittingStartedAt?: string
  messageCount?: number
  /** User-facing AI summary (written to be read by the user). */
  summary?: string
  /** Short auto title for the conversation list. */
  title?: string
  /** When the latest attempt to close it, once it had gone quiet, began. */
  autoCloseClaimedAt?: string
  /** How many times closing it after it went quiet has been tried. */
  autoCloseAttempts?: number
  /** When closing it was given up on, after its last attempt; the one alert about it went out then. */
  autoCloseGivenUpAt?: string
  /** When the sweep closed it once it had gone quiet; its owner writing to it again opens it back up. */
  autoClosedAt?: string
}

/** Who closes a conversation: the person with Stop, or the sweep once it has gone quiet. */
export enum CoachConversationCloser {
  PERSON = 'person',
  QUIET_SWEEP = 'quiet-sweep'
}

/** What happened to one quiet conversation in a run that closes them. */
export enum IdleCoachConversationOutcome {
  CLOSED = 'closed',
  SKIPPED = 'skipped',
  /** They spoke while it was closing, so it stays open until it goes quiet again. */
  LEFT_OPEN = 'left-open',
  FAILED = 'failed',
  /** Failed on its last attempt; no later run tries again. */
  GIVEN_UP = 'given-up'
}

/**
 * Per-user AI "brain": durable, version/model-agnostic memory ABOUT the user, reusable by any
 * future AI feature. Stored in `user_ai_agents` keyed by userId. Keep it small — only what is
 * genuinely useful as future-session reference.
 */
export type UserAiAgent = BaseObject & {
  userId: string
  /** Curated long-lived facts the user asked us to remember / that meaningfully describe them. */
  memories: UserAiMemory[]
  /** Rolling short synthesis of who this user is and where they are in their development. */
  profileSummary?: string
  /** Misuse counter for off-topic abuse; decays over time (see coach config). */
  misuse?: CoachMisuseState
  updatedAt?: string
}

/** A coaching preference is how they asked to be coached; anything else they told her is a fact. */
export enum UserAiMemoryKind {
  FACT = 'fact',
  COACHING_PREFERENCE = 'coaching_preference'
}

export type UserAiMemory = {
  id: string
  text: string
  /** Absent means a fact. */
  kind?: UserAiMemoryKind
  createdAt: string
  /** Origin conversation, if any. */
  sourceConversationId?: string
  /** Which of the two coaching tracks it came from; absent means individual. Leadership memories are withheld from a former leader. */
  scope?: GoalScope
}

export type CoachMisuseState = {
  count: number
  windowStartedAt: string
  /** When set and in the future, coach access is paused. */
  pausedUntil?: string
}

/** Per-user coach state stored on the User doc. */
export type UserCoachState = {
  /** Set when the onboarding goal-setting flow completes (flips the onboarding step). */
  onboardingGoalsCompletedAt?: string
  /** Whether a facilitator/admin has re-requested a fresh goal-setting round. */
  goalSettingRequestedAt?: string
}
